// generationLedger — per-user memory of what the GENERATOR drafted
// (roadmap A5+B5; migration 0284). cook_logs remembers cooks; this
// remembers drafts, so cross-session surprise-me / dream runs stop
// re-treading the same titles and flavor territories after a tab
// close wipes previousTitles.
//
// Egress discipline: reads are user-scoped `limit(N)` selects of a
// few text columns (~1KB); writes are one row per PAID final
// generation — both rounding errors next to the Sonnet call that
// triggers them. Writes are fire-and-forget: a ledger failure must
// never block the recipe landing.

import { supabase } from "./supabase";

const READ_LIMIT = 15;

// Dominant taste axes for the ledger's taste_tags — same enrichment
// scales the balance critic reads, tallied per-recipe so future
// steering can push toward loved territories ("this user nails
// acid-forward dishes"). Threshold ≥2 sources keeps tags meaningful.
function dominantTasteTags(recipe, getInfo) {
  const ings = Array.isArray(recipe?.ingredients) ? recipe.ingredients : [];
  const tally = { acid: 0, fat: 0, sweet: 0, salt: 0, heat: 0, umami: 0 };
  for (const ing of ings) {
    const id = ing?.ingredientId;
    if (!id) continue;
    let fp = null;
    try { fp = typeof getInfo === "function" ? getInfo(id)?.flavor_profile : null; } catch { /* optional */ }
    if (!fp) continue;
    if (fp.sour  >= 3) tally.acid  += 1;
    if (fp.fat   >= 3) tally.fat   += 1;
    if (fp.sweet >= 3) tally.sweet += 1;
    if (fp.salty >= 3) tally.salt  += 1;
    if (fp.heat  >= 2) tally.heat  += 1;
    if (fp.umami >= 3) tally.umami += 1;
  }
  return Object.entries(tally)
    .filter(([, n]) => n >= 2)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([axis]) => axis);
}

/**
 * Record a landed final generation. Fire-and-forget — callers don't
 * await this on the render path.
 */
export function recordGeneration({ userId, recipe, frame, getInfo }) {
  if (!userId || !recipe?.title) return;
  const row = {
    user_id: userId,
    title: String(recipe.title).slice(0, 200),
    cuisine: recipe.cuisine ? String(recipe.cuisine).slice(0, 60) : null,
    hero_ids: frame?.hero?.id ? [frame.hero.id] : [],
    technique_tags: frame?.technique?.id ? [frame.technique.id] : [],
    taste_tags: dominantTasteTags(recipe, getInfo),
  };
  supabase
    .from("recipe_generation_ledger")
    .insert(row)
    .then(({ error }) => {
      if (error) console.warn("[generationLedger] insert failed:", error.message);
    })
    .catch((e) => console.warn("[generationLedger] insert failed:", e?.message || e));
}

/**
 * Recent generated titles for cross-session avoidTitles. Returns []
 * on any failure — steering signal, never a blocker.
 */
export async function fetchRecentGenerationTitles(userId, limit = READ_LIMIT) {
  if (!userId) return [];
  try {
    const { data, error } = await supabase
      .from("recipe_generation_ledger")
      .select("title")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(limit);
    if (error) return [];
    return (data || []).map((r) => r.title).filter(Boolean);
  } catch {
    return [];
  }
}
